import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Send, 
  Edit, 
  Trash2, 
  Reply, 
  Clock, 
  User,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const FolderComments = ({ folderId, folderName, folderPath }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    if (folderId) {
      fetchComments();
    }
  }, [folderId]);

  const fetchComments = async () => {
    try {
      setError('');
      const response = await api.get(`/comments/folder/${folderId}`);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('Hiba történt a megjegyzések betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/comments/folder/${folderId}`, {
        content: newComment.trim()
      });
      
      setComments([response.data.comment, ...comments]);
      setNewComment('');
      setError('');
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Hiba történt a megjegyzés hozzáadása során.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editContent.trim()) return;

    try {
      const response = await api.put(`/comments/comment/${commentId}`, {
        content: editContent.trim()
      });
      
      setComments(comments.map(comment => 
        comment.id === commentId ? response.data.comment : comment
      ));
      setEditingComment(null);
      setEditContent('');
      setError('');
    } catch (error) {
      console.error('Error editing comment:', error);
      setError('Hiba történt a megjegyzés szerkesztése során.');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Biztosan törölni szeretné ezt a megjegyzést?')) return;

    try {
      await api.delete(`/comments/comment/${commentId}`);
      setComments(comments.filter(comment => comment.id !== commentId));
      setError('');
    } catch (error) {
      console.error('Error deleting comment:', error);
      setError('Hiba történt a megjegyzés törlése során.');
    }
  };

  const handleSubmitReply = async (commentId) => {
    if (!replyContent.trim()) return;

    try {
      const response = await api.post(`/comments/reply/${commentId}`, {
        content: replyContent.trim()
      });
      
      setComments(comments.map(comment => 
        comment.id === commentId 
          ? { ...comment, replies: [...comment.replies, response.data.reply] }
          : comment
      ));
      setReplyingTo(null);
      setReplyContent('');
      setError('');
    } catch (error) {
      console.error('Error adding reply:', error);
      setError('Hiba történt a válasz hozzáadása során.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canEditOrDelete = (comment) => {
    return user.role === 'admin' || comment.author.id === user.id;
  };

  const canReply = () => {
    return user.role === 'admin';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <MessageSquare className="h-6 w-6 text-primary-600" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Megjegyzések
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {folderPath || folderName || 'Gyökér könyvtár'}
          </p>
        </div>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {comments.length}
        </span>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* New Comment Form */}
      <form onSubmit={handleSubmitComment} className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-primary-600" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <textarea
              rows={3}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Írjon megjegyzést ehhez a mappához..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              disabled={submitting}
            />
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Megjegyzés hozzáadása: {user.username}
              </p>
              <button
                type="submit"
                disabled={submitting || !newComment.trim()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? 'Küldés...' : 'Küldés'}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Comments List */}
      {comments.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Még nincsenek megjegyzések</h4>
          <p className="text-gray-600">Legyen az első, aki megjegyzést fűz ehhez a mappához!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="bg-white rounded-lg border border-gray-200 p-4">
              {/* Comment Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {comment.author.username}
                      {comment.author.role === 'admin' && (
                        <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Admin
                        </span>
                      )}
                    </p>
                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(comment.createdAt)}</span>
                      {comment.updatedAt !== comment.createdAt && (
                        <span className="italic">(szerkesztve)</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {canEditOrDelete(comment) && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setEditingComment(comment.id);
                        setEditContent(comment.content);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      title="Szerkesztés"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Törlés"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Comment Content */}
              {editingComment === comment.id ? (
                <div className="space-y-3">
                  <textarea
                    rows={3}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditComment(comment.id)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                    >
                      Mentés
                    </button>
                    <button
                      onClick={() => {
                        setEditingComment(null);
                        setEditContent('');
                      }}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Mégse
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-gray-700 whitespace-pre-wrap mb-3">{comment.content}</p>
                  
                  {canReply() && (
                    <button
                      onClick={() => setReplyingTo(comment.id)}
                      className="inline-flex items-center text-sm text-gray-500 hover:text-primary-600"
                    >
                      <Reply className="h-4 w-4 mr-1" />
                      Válasz
                    </button>
                  )}
                </div>
              )}

              {/* Reply Form */}
              {replyingTo === comment.id && (
                <div className="mt-4 ml-8">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <textarea
                      rows={2}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      placeholder="Válasz írása..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                    />
                    <div className="mt-2 flex space-x-2">
                      <button
                        onClick={() => handleSubmitReply(comment.id)}
                        disabled={!replyContent.trim()}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                      >
                        Küldés
                      </button>
                      <button
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyContent('');
                        }}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Mégse
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Replies */}
              {comment.replies && comment.replies.length > 0 && (
                <div className="mt-4 ml-8 space-y-3">
                  {comment.replies.map((reply) => (
                    <div key={reply.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-3 w-3 text-blue-600" />
                        </div>
                        <p className="text-sm font-medium text-gray-900">
                          {reply.author.username}
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Admin
                          </span>
                        </p>
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Clock className="h-3 w-3" />
                          <span>{formatDate(reply.createdAt)}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{reply.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FolderComments; 